import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { toast } from 'react-toastify';
import {
  useSniperGroups,
  useCreateSniperGroup,
  useDeleteSniperGroup,
  useUpsertSniperEvent,
  usePanicAll,
  useClearGlobalHalt,
} from '../api/useSniper';
import { SniperEventCard } from '../components/sniper/SniperEventCard';
import { SniperGroupsLanding } from '../components/sniper/SniperGroupsLanding';
import { SniperGroupControls } from '../components/sniper/SniperGroupControls';
import { anySniperHalt } from '../utils/sniperGroupState';
import type { SniperGroup } from '../types/sniper.types';

/**
 * The Sniper page: a groups LANDING (list of groups, each with a state badge +
 * per-group Start(test|normal)/Stop/Panic), plus a GLOBAL panic + halted
 * indicator in the header. Clicking a group opens its DETAIL — the
 * Group→Event→Market view (live state line + ARMED badges + per-market draft
 * params + enabled toggle). Firing is gated by the group being Started; there
 * is no single master switch. Its own page (`/predictions/sniper`).
 */
export function SniperPage() {
  const { data: groups = [], isLoading, isError } = useSniperGroups();
  const createGroup = useCreateSniperGroup();
  const deleteGroup = useDeleteSniperGroup();
  const upsertEvent = useUpsertSniperEvent();
  const panicAll = usePanicAll();
  const clearGlobalHalt = useClearGlobalHalt();

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [confirmPanicAll, setConfirmPanicAll] = useState(false);

  const selectedGroup = useMemo(
    () => groups.find((g) => g._id === selectedGroupId) ?? null,
    [groups, selectedGroupId],
  );

  const halted = anySniperHalt(groups);

  // ── add-event form (scoped to the selected group) ─────────────────────────
  const [newEvent, setNewEvent] = useState({
    fixtureId: '',
    homeTeam: '',
    awayTeam: '',
    league: '',
  });
  const newFixtureIdNum = Number(newEvent.fixtureId);
  const canAddEvent =
    !!selectedGroup &&
    Number.isInteger(newFixtureIdNum) &&
    newFixtureIdNum > 0 &&
    newEvent.homeTeam.trim().length > 0 &&
    newEvent.awayTeam.trim().length > 0;

  const addEvent = () => {
    if (!canAddEvent || !selectedGroup) return;
    upsertEvent.mutate(
      {
        groupId: selectedGroup._id,
        dto: {
          fixtureId: newFixtureIdNum,
          homeTeam: newEvent.homeTeam.trim(),
          awayTeam: newEvent.awayTeam.trim(),
          league: newEvent.league.trim() || null,
        },
      },
      {
        onSuccess: () =>
          setNewEvent({ fixtureId: '', homeTeam: '', awayTeam: '', league: '' }),
        onError: () => {
          toast.error('Failed to add event.');
        },
      },
    );
  };

  const addGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    createGroup.mutate(
      { name, sport: 'soccer' },
      {
        onSuccess: () => setNewGroupName(''),
        onError: () => {
          toast.error('Failed to create group.');
        },
      },
    );
  };

  const removeGroup = (group: SniperGroup) => {
    if (
      !window.confirm(
        `Delete group “${group.name}” and ALL its markets? This cannot be undone.`,
      )
    ) {
      return;
    }
    deleteGroup.mutate(group._id, {
      onSuccess: () => {
        if (group._id === selectedGroupId) setSelectedGroupId(null);
      },
      onError: () => {
        toast.error('Failed to delete group.');
      },
    });
  };

  const confirmPanicAllRun = () => {
    setConfirmPanicAll(false);
    panicAll.mutate(undefined, {
      onError: () => {
        toast.error('Failed to panic all groups.');
      },
    });
  };

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <GpsFixedIcon sx={{ color: halted ? '#ff5252' : 'text.secondary' }} />
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Sniper
        </Typography>
        {halted && (
          <Chip
            size="small"
            color="error"
            label="HALTED"
            sx={{ fontWeight: 800 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {halted && (
          <Tooltip title="Clear the global halt on ALL groups. Does NOT resume — Start each group after.">
            <span>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={
                  clearGlobalHalt.isLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <LockOpenIcon />
                  )
                }
                onClick={() =>
                  clearGlobalHalt.mutate(undefined, {
                    onError: () => {
                      toast.error('Failed to clear global halt.');
                    },
                  })
                }
                disabled={clearGlobalHalt.isLoading}
              >
                Clear halt (all)
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Immediately PANIC every Sniper group + cancel all orders">
          <span>
            <Button
              variant="contained"
              color="error"
              startIcon={
                panicAll.isLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <WarningAmberIcon />
                )
              }
              onClick={() => setConfirmPanicAll(true)}
              disabled={panicAll.isLoading}
            >
              PANIC ALL
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {halted && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
          One or more Sniper groups are HALTED. Clearing a halt does NOT resume —
          a halted group stays stopped until it is Started again.
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Failed to load Sniper groups.
          </Alert>
        )}
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}

        {!selectedGroup ? (
          // ── LANDING: create group + list of groups ────────────────────────
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'center' }}
              >
                <TextField
                  size="small"
                  label="New group name"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  sx={{ minWidth: 200 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={addGroup}
                  disabled={!newGroupName.trim() || createGroup.isLoading}
                >
                  Add group
                </Button>
              </Stack>
            </Paper>

            {!isLoading && groups.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No groups yet — create one to start adding matches.
              </Typography>
            )}

            <SniperGroupsLanding
              groups={groups}
              onSelectGroup={setSelectedGroupId}
              onDeleteGroup={removeGroup}
              deleteBusy={deleteGroup.isLoading}
            />
          </>
        ) : (
          // ── DETAIL: selected group → events → markets ─────────────────────
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Button
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setSelectedGroupId(null)}
                  >
                    All groups
                  </Button>
                  <Typography
                    variant="h6"
                    sx={{ color: 'common.white', fontWeight: 600 }}
                  >
                    {selectedGroup.name}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <SniperGroupControls group={selectedGroup} />
                </Stack>

                <Divider flexItem />

                {/* Add an event (match) by LSports fixture ID. */}
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ md: 'center' }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 80 }}
                  >
                    Add match
                  </Typography>
                  <TextField
                    size="small"
                    label="Fixture ID"
                    value={newEvent.fixtureId}
                    onChange={(e) =>
                      setNewEvent((m) => ({
                        ...m,
                        fixtureId: e.target.value.replace(/[^0-9]/g, ''),
                      }))
                    }
                    inputProps={{ inputMode: 'numeric' }}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    size="small"
                    label="Home team"
                    value={newEvent.homeTeam}
                    onChange={(e) =>
                      setNewEvent((m) => ({ ...m, homeTeam: e.target.value }))
                    }
                    sx={{ flex: 1, minWidth: 140 }}
                  />
                  <TextField
                    size="small"
                    label="Away team"
                    value={newEvent.awayTeam}
                    onChange={(e) =>
                      setNewEvent((m) => ({ ...m, awayTeam: e.target.value }))
                    }
                    sx={{ flex: 1, minWidth: 140 }}
                  />
                  <TextField
                    size="small"
                    label="League (optional)"
                    value={newEvent.league}
                    onChange={(e) =>
                      setNewEvent((m) => ({ ...m, league: e.target.value }))
                    }
                    sx={{ flex: 1, minWidth: 130 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={addEvent}
                    disabled={!canAddEvent || upsertEvent.isLoading}
                  >
                    {upsertEvent.isLoading ? 'Adding…' : 'Add'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {(selectedGroup.events?.length ?? 0) === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No matches in this group — add one above.
              </Typography>
            )}
            <Stack spacing={2}>
              {(selectedGroup.events ?? []).map((event) => (
                <SniperEventCard key={event.fixtureId} event={event} />
              ))}
            </Stack>
          </>
        )}
      </Box>

      <Dialog open={confirmPanicAll} onClose={() => setConfirmPanicAll(false)}>
        <DialogTitle>PANIC every Sniper group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This immediately halts EVERY Sniper group and cancels all their
            orders. Groups stay halted until you clear the halt AND Start them
            again. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPanicAll(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmPanicAllRun}>
            PANIC ALL
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
